import { createPortal } from 'react-dom';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

const css = `
  .cfm-overlay{position:fixed;inset:0;background:rgba(10,12,28,.55);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:2000;padding:20px;font-family:'Sora',sans-serif;}
  .cfm-overlay *{box-sizing:border-box;}
  .cfm-modal{background:white;border-radius:20px;width:100%;max-width:400px;max-height:90vh;border:1px solid #e8ebf4;box-shadow:0 24px 64px rgba(0,0,0,.18);overflow:hidden;display:flex;flex-direction:column;animation:cfmIn .25s ease;}
  @keyframes cfmIn{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:none}}
  .cfm-hdr{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #f0f2f8;flex-shrink:0;gap:8px;}
  .cfm-hdr-l{display:flex;align-items:center;gap:10px;}
  .cfm-icon{width:34px;height:34px;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .cfm-icon svg{width:17px;height:17px;stroke-width:1.8;}
  .cfm-icon.red{background:#fef2f2;color:#ef4444;}
  .cfm-icon.blue{background:#eef2ff;color:#3b6ef8;}
  .cfm-title{font-size:.95rem;font-weight:700;color:#0f1123;}
  .cfm-sub{font-size:.72rem;color:#9094a8;}
  .cfm-close{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;}
  .cfm-close:disabled{opacity:.5;cursor:not-allowed;}
  .cfm-close svg{width:14px;height:14px;stroke-width:2;}
  .cfm-body{padding:22px;overflow-y:auto;flex:1;font-size:.82rem;color:#3a3f58;line-height:1.6;}
  .cfm-foot{display:flex;justify-content:flex-end;gap:8px;padding:16px 22px;border-top:1px solid #f0f2f8;flex-shrink:0;}
  .cfm-warn-box{background:#fef2f2;border:1px solid #fecaca;border-radius:10px;padding:10px 14px;font-size:.76rem;color:#991b1b;line-height:1.55;}
  .cfm-btn{padding:8px 16px;border-radius:10px;font-size:.8rem;font-weight:600;font-family:'Sora',sans-serif;cursor:pointer;border:none;display:inline-flex;align-items:center;gap:7px;transition:.15s;}
  .cfm-btn:disabled{opacity:.5;cursor:not-allowed;}
  .cfm-btn-ghost{background:#f0f2f7;color:#6b7194;border:1px solid #e0e4f0;}
  .cfm-btn-ghost:hover:not(:disabled){background:#e8ebf4;}
  .cfm-btn-red{background:#ef4444;color:white;}
  .cfm-btn-red:hover:not(:disabled){box-shadow:0 4px 14px rgba(239,68,68,.35);}
  .cfm-btn-blue{background:#3b6ef8;color:white;}
  .cfm-btn-blue:hover:not(:disabled){box-shadow:0 4px 14px rgba(59,110,248,.35);}
`;

/**
 * Reusable portal-based confirmation dialog. Visual style mirrors
 * ParentManagement.jsx's "Deactivate Modal", but carries its own scoped
 * inline <style> (cfm- prefixed classes) so it works from any page.
 *
 * Props:
 *  - open: bool
 *  - title: string
 *  - subtitle: string (optional, e.g. the person's name)
 *  - message: string | node (the warning/body text)
 *  - confirmLabel: string (default "Confirm")
 *  - danger: bool (red confirm button when true, blue/accent otherwise)
 *  - onConfirm: async fn
 *  - onCancel: fn
 *  - loading: bool (disables buttons, shows "Saving…" on confirm button)
 */
export default function ConfirmModal({
  open,
  title,
  subtitle,
  message,
  confirmLabel = 'Confirm',
  danger = false,
  onConfirm,
  onCancel,
  loading = false,
}) {
  if (!open) return null;

  return createPortal(
    <>
      <style>{css}</style>
      <div className="cfm-overlay" onClick={e => e.target === e.currentTarget && !loading && onCancel && onCancel()}>
        <div className="cfm-modal">
          <div className="cfm-hdr">
            <div className="cfm-hdr-l">
              <div className={`cfm-icon ${danger ? 'red' : 'blue'}`}><ExclamationTriangleIcon/></div>
              <div>
                <div className="cfm-title">{title}</div>
                {subtitle && <div className="cfm-sub">{subtitle}</div>}
              </div>
            </div>
            <button className="cfm-close" onClick={onCancel} disabled={loading}><XMarkIcon/></button>
          </div>
          <div className="cfm-body">
            {danger
              ? <div className="cfm-warn-box">{message}</div>
              : <div>{message}</div>
            }
          </div>
          <div className="cfm-foot">
            <button className="cfm-btn cfm-btn-ghost" onClick={onCancel} disabled={loading}>Cancel</button>
            <button
              className={`cfm-btn ${danger ? 'cfm-btn-red' : 'cfm-btn-blue'}`}
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? 'Saving…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
